/**
 * PermissionGuard — Route Guard для проверки прав доступа (Story 21.1)
 *
 * Использование в app.routes.ts:
 *   {
 *     path: 'settings/team',
 *     component: TeamComponent,
 *     canActivate: [PermissionGuard('team.view')],
 *   }
 *
 * При недостаточных правах:
 * - Редиректит на /dashboard
 * - Показывает toast "Недостаточно прав"
 *
 * Story 21.9 fix: Guard is now async and waits for auth to be loaded.
 * On page reload, Keycloak initializes asynchronously (~500-2000ms).
 * Without this wait, guard sees currentUser()=null → role='viewer' → redirect.
 */

import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Permission, PermissionService } from '../services/permission.service';
import { AuthService } from '../services/auth.service';

export function PermissionGuard(permission: Permission): CanActivateFn {
  return async () => {
    // ВАЖНО: все inject() вызовы ОБЯЗАНЫ быть ДО любого await
    // (injection context теряется после первого await)
    const permissionService = inject(PermissionService);
    const authService = inject(AuthService);
    const router = inject(Router);
    const messageService = inject(MessageService, { optional: true });

    // Ждём загрузки auth (Keycloak async initialization на полной перезагрузке страницы).
    // isAuthenticated становится true после loadKeycloakUser() → cabinets loaded + user set.
    // Максимум 5 секунд — после этого продолжаем с текущим состоянием.
    if (!authService.isAuthenticated()) {
      const startTime = Date.now();
      while (!authService.isAuthenticated() && Date.now() - startTime < 5000) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    if (permissionService.can(permission)) {
      return true;
    }

    // Показать toast если MessageService зарегистрирован (optional dependency)
    if (messageService) {
      messageService.add({
        severity: 'warn',
        summary: 'Недостаточно прав',
        detail: `Раздел недоступен для вашей роли (${permissionService.role()})`,
        life: 4000,
      });
    }

    return router.createUrlTree(['/dashboard']);
  };
}
